import React from "react";

// Simple line drawings, one per kind of site, so each stop gets its own "banner".
function Building({ kind }) {
  const common = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  const cross = (x, y) => (
    <path {...common} d={`M${x} ${y - 4}v8M${x - 4} ${y}h8`} />
  );

  if (kind === "pediatrics") {
    return (
      <svg viewBox="0 0 120 64" aria-hidden="true" className="h-full w-auto">
        <path {...common} d="M6 62h108" />
        <path {...common} d="M20 62V30l24-18 24 18v32" />
        <path {...common} d="M68 62V36h36v26" />
        <path {...common} d="M38 62V48h12v14" />
        <rect {...common} x="76" y="42" width="8" height="8" />
        <rect {...common} x="90" y="42" width="8" height="8" />
        {cross(44, 32)}
        <circle {...common} cx="104" cy="14" r="6" />
        <path {...common} d="M104 20c-1 6 2 10 0 16" />
      </svg>
    );
  }

  if (kind === "hospital") {
    return (
      <svg viewBox="0 0 120 64" aria-hidden="true" className="h-full w-auto">
        <path {...common} d="M4 62h112" />
        <path {...common} d="M10 62V34h22v28M88 62V34h22v28" />
        <path {...common} d="M32 62V14h56v48" />
        <path {...common} d="M52 62V50h16v12" />
        {[24, 36].map((y) =>
          [40, 56, 72].map((x) => <rect key={`${x}-${y}`} {...common} x={x - 4} y={y} width="8" height="6" />)
        )}
        {cross(60, 6)}
        <path {...common} d="M16 42h10M16 50h10M94 42h10M94 50h10" />
      </svg>
    );
  }

  if (kind === "health-center") {
    return (
      <svg viewBox="0 0 120 64" aria-hidden="true" className="h-full w-auto">
        <path {...common} d="M4 62h112" />
        <path {...common} d="M14 62V22h92v40" />
        <path {...common} d="M10 22h100" />
        <path {...common} d="M46 40h28l-4 6H50z" />
        <path {...common} d="M52 62V46h16v16" />
        <rect {...common} x="22" y="30" width="14" height="10" />
        <rect {...common} x="84" y="30" width="14" height="10" />
        <rect {...common} x="22" y="48" width="14" height="8" />
        <rect {...common} x="84" y="48" width="14" height="8" />
        {cross(60, 31)}
        <path {...common} d="M104 62c0-8 6-10 6-18 0-6-6-6-6 0" />
      </svg>
    );
  }

  if (kind === "office") {
    return (
      <svg viewBox="0 0 120 64" aria-hidden="true" className="h-full w-auto">
        <path {...common} d="M4 62h112" />
        <path {...common} d="M40 62V6h40v56" />
        <path {...common} d="M80 62V26h26v36M14 62V34h26v28" />
        {[14, 24, 34, 44].map((y) => (
          <path key={y} {...common} d={`M48 ${y}h24`} />
        ))}
        <path {...common} d="M54 62V54h12v8" />
        <path {...common} d="M86 36h14M86 46h14M20 44h14M20 52h14" />
      </svg>
    );
  }

  // Generic clinic
  return (
    <svg viewBox="0 0 120 64" aria-hidden="true" className="h-full w-auto">
      <path {...common} d="M4 62h112" />
      <path {...common} d="M18 62V28h84v34" />
      <path {...common} d="M12 28l12-12h72l12 12" />
      <path {...common} d="M52 62V46h16v16" />
      <rect {...common} x="26" y="38" width="14" height="10" />
      <rect {...common} x="80" y="38" width="14" height="10" />
      {cross(60, 22)}
    </svg>
  );
}

function OnsiteRoute({ stops }) {
  return (
    <ol className="relative flex flex-col gap-3" aria-label="Provider sites visited">
      {/* dashed route line connecting the stops */}
      <span
        aria-hidden="true"
        className="absolute left-[15px] top-6 bottom-6 border-l-2 border-dashed border-[#eb4d5c]/60 dark:border-[#c9a7ff]/60"
      />
      {stops.map((stop, i) => (
        <li key={stop.site} className="relative flex items-center gap-3">
          <span
            aria-hidden="true"
            className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#eb4d5c] text-sm font-bold text-white ring-4 ring-[#f0f0f0] dark:bg-[#c9a7ff] dark:text-[#0f193d] dark:ring-[#3d4851]"
          >
            {i + 1}
          </span>
          <div className="relative flex-1 overflow-hidden rounded-xl border border-[#eb4d5c]/25 bg-white/80 shadow-sm dark:border-[#c9a7ff]/25 dark:bg-white/5">
            {/* the clinic drawing sits faded on the right, like a banner */}
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-end py-2 pr-3 text-[#eb4d5c]/30 dark:text-[#c9a7ff]/30">
              <div className="h-9 sm:h-14">
                <Building kind={stop.kind} />
              </div>
            </div>
            <div className="relative flex min-h-[4.25rem] items-center gap-3 px-3 sm:px-4 py-3 pr-16 sm:pr-36">
              <span className="w-9 shrink-0 text-xl font-extrabold leading-none tracking-wider text-[#6b2fa0] dark:text-[#c9a7ff]">
                {stop.state}
              </span>
              <span className="flex flex-col">
                <span className="text-[15px] sm:text-lg font-semibold leading-snug">{stop.site}</span>
                <span className="text-sm text-[#555] dark:text-[#c9cfe3]">
                  {stop.city}
                  {stop.pictured && (
                    <span className="ml-2 rounded-full bg-[#eb4d5c]/10 px-2 py-0.5 text-xs font-semibold text-[#eb4d5c] dark:bg-[#c9a7ff]/15 dark:text-[#c9a7ff]">
                      Pictured
                    </span>
                  )}
                </span>
              </span>
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}

export default OnsiteRoute;
